import React, { useContext, useEffect, useState } from "react"
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import { translateSymptom, SYMPTOM_CATEGORIES } from "../../i18n/translateData";

import { Row, Col } from "react-bootstrap";
import Tab from 'react-bootstrap/Tab';
import Nav from 'react-bootstrap/Nav';

import styled from "styled-components"

import {
    addTrackedFactorsUnpaused,
    removePausedFactor,
} from "../../api/TBIRequests";

const Container = styled.div`
  // width: 280px;
  margin: 10px;
  h6 {
    text-align: center;
  }
  ul {
    font-weight: normal;
    font-size: 15px;
    margin: 10px;
  }
`
const UlPausedList = ({ symptoms, title, setPauseList, pausedFactorObj, setPausedFactorObj, setTopSymptoms, topSymptomsList, lastPaused, setLastUnpaused }) => {
    const { patient } = useContext(PatientContext);
    const { t } = useTranslation();

    // Controlled tab selection (null = fall back to the first category with paused symptoms)
    const [activeKey, setActiveKey] = useState(null);
    // Factor of the symptom that was just paused — briefly highlighted
    const [highlighted, setHighlighted] = useState(null);

    // When a symptom is paused, jump to its category and flash it for 3s
    useEffect(() => {
        if (!lastPaused?.subcategory) return;
        setActiveKey(lastPaused.subcategory);
        setHighlighted(lastPaused.factor);
        const timer = setTimeout(() => setHighlighted(null), 3000);
        return () => clearTimeout(timer);
    }, [lastPaused]);

    const removePauseF = (factor) => {
        let newParsedFactors = [...symptoms];
        if (newParsedFactors) {
            if (newParsedFactors.includes(factor)) {
                const index = newParsedFactors.indexOf(factor);
                if (index > -1) { // only splice array when item is found
                    newParsedFactors.splice(index, 1); // 2nd parameter means remove one item only
                    setPauseList(newParsedFactors);
                }
            }
        }

        removePausedFactor({
            patientId: patient.patientId,
            data: [factor],
        })
            .catch(() => {
                console.log("removePausedFactor error");
            })
    }

    const tabs = Object.fromEntries(
        SYMPTOM_CATEGORIES.map((c) => [c, t(`onboarding.symptomSelector.category.${c}`)])
    );

    // Display only — the English `factor` is what's sent to the backend.
    // Apostrophe mismatches are handled inside translateSymptom.
    const translateSymptomLabel = (factor, subcategory) => translateSymptom(t, factor, subcategory);

    const addTracking = (factor) => {
        const objWithFactor = pausedFactorObj?.find((symptom) => symptom.factor === factor);
        const filteredFactorsList = pausedFactorObj?.filter(symptom => symptom.factor !== factor);
        setPausedFactorObj(filteredFactorsList)

        let newTopSymptoms = [...topSymptomsList];
        newTopSymptoms.push(objWithFactor);
        setTopSymptoms(newTopSymptoms);
        // Tell the "Currently Tracking" section which symptom was just unpaused,
        // so it can switch to that category and highlight it.
        // unpausedAt makes unpausing the same symptom again still retrigger it.
        if (setLastUnpaused && objWithFactor) {
            setLastUnpaused({ ...objWithFactor, unpausedAt: Date.now() });
        }
        addTrackedFactorsUnpaused({
            patientId: patient.patientId,
            data: [[factor]],
        }).catch(() => {
            console.log("addTrackedFactorsUnpaused error");
        })
    }

    // Create a Set to store unique subcategories
    const uniqueSubcategories = new Set();

    // Filter and populate the Set with unique subcategories
    pausedFactorObj.forEach((symptom) => {
        uniqueSubcategories.add(symptom.subcategory);
    });

    // Convert the Set to an array (if needed)
    const uniqueSubcategoryArray = Array.from(uniqueSubcategories).sort(
        (a, b) => Object.keys(tabs).indexOf(a) - Object.keys(tabs).indexOf(b)
    );

    // Never show an empty pane: if the selected category has no paused symptoms
    // (or nothing was selected yet), fall back to the first category that does.
    const resolvedKey = uniqueSubcategoryArray.includes(activeKey)
        ? activeKey
        : uniqueSubcategoryArray[0];

    const colors = {
        motor: "#b3e5fc",
        sensory: "#ffccbc",
        emotional: "#dab7ec",
        behavioral: "#ffdadb",
        cognitive: "#ffdcaf",
        immune: "#fff3c4",
        metabolic: "#cef3c4",

        // for trigger
        wellness: "#dab7ec",
        travel: "#ffdadb",
        "screen time": "#ffdcaf",
        environmental: "#fff3c4",
        Dietary: "#cef3c4",
        situational: "#c1fdf7",

        // for therapies
        Alternative: "#dab7ec",
        Applied: "#ffdadb",
        Chiropractic: "#ffdcaf",
        Vision: "#fff3c4",
        "Mental Health": "#c1fdf7",

        Other: "#D3D3D3",
    };

    return (
        <Container>
            <h6>{title}</h6>
            {uniqueSubcategoryArray.length > 1 && (
                <p style={{ textAlign: "center", fontSize: "13px", color: "#666", margin: "0 0 0.5rem" }}>
                    {t("dashboard.addSymptoms.pausedHint")}
                </p>
            )}
            <Tab.Container
                id="paused-symptoms-tabs"
                activeKey={resolvedKey}
                onSelect={(k) => setActiveKey(k)}
            >
                <Row style={{ marginTop: "0.5rem", fontWeight: "normal", fontSize: "15px" }}>
                    <Col sm={3} style={{ paddingRight: '0px' }}>
                        <Nav fill justify variant="tabs" className="flex-column">
                            {uniqueSubcategoryArray.map((subcategory, i) => (
                                <Nav.Item key={i}
                                >
                                    <Nav.Link className="navLink" eventKey={subcategory}
                                        style={{
                                            background: `${colors[subcategory]}`,
                                            border: subcategory === resolvedKey ? '3px solid #106995' : '1px solid #106995',
                                            fontWeight: subcategory === resolvedKey ? 700 : 400,
                                        }}> {tabs[subcategory]}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Col>

                    <Col sm={7} style={{
                        marginLeft: '1rem', paddingRight: '0px'
                    }}>
                        <Tab.Content >
                            {
                                uniqueSubcategoryArray.map((subcategory, i) => (
                                    <Tab.Pane eventKey={subcategory} key={i}>
                                        <h6 className="d-inline">{tabs[subcategory]}</h6>
                                        <ul>
                                            {pausedFactorObj
                                                .filter((symptom) => symptom.subcategory === subcategory)
                                                .map((filteredSymptom, j) => (
                                                    <li
                                                        key={j}
                                                        style={{
                                                            backgroundColor: filteredSymptom.factor === highlighted ? '#fff3c4' : 'transparent',
                                                            transition: 'background-color 0.6s ease',
                                                            borderRadius: '4px',
                                                            padding: '2px 4px',
                                                        }}
                                                    >
                                                        {translateSymptomLabel(filteredSymptom.factor, subcategory)}{" "}
                                                        <button
                                                            onClick={() => {
                                                                removePauseF(filteredSymptom.factor)
                                                                addTracking(filteredSymptom.factor)
                                                            }}
                                                            style={{ background: '#d5f1c8' }}
                                                        >
                                                            {t("dashboard.unpause")}
                                                        </button>
                                                    </li>
                                                ))}
                                        </ul>
                                    </Tab.Pane>
                                ))
                            }
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container >
        </Container >
    )
}

export default UlPausedList